import React, { useState } from "react";
import { Box } from "@mui/joy";
import { theme } from "styles/theme";
import CustomModal from "components/common/CustomModal";
import type { TaskEditorState } from "../utils/taskEditorUtils";
import { stableHashBlocks } from "../utils/taskEditorDiff";
import type { AssistantTurn, Decision } from "./types";
import { ApprovalCard, ApprovalDecisionRow } from "./approval";
import { TextStreamBubble } from "./bubbles";
import { formatDuration, ToolCallRow } from "./toolCallRow";
import { ThinkingIndicator } from "./thinkingIndicator";
import { TodoChecklist } from "./todoChecklist";
import { TurnDiffModal } from "./TurnDiffModal";

// =========================
// TaskPatchPill — 변경사항 보기 / 이 턴 되돌리기 액션 포함
// =========================

interface TaskPatchPillProps {
  // taskBefore 가 있고 현재 task 와 hash 가 달라야 diff 가 의미 있음 (noop 정정).
  taskBefore?: TaskEditorState;
  currentTask?: TaskEditorState;
  onRevert?: () => void;
}

export function TaskPatchPill({
  taskBefore,
  currentTask,
  onRevert,
}: TaskPatchPillProps) {
  const [diffOpen, setDiffOpen] = useState(false);
  const [confirmRevertOpen, setConfirmRevertOpen] = useState(false);
  // hash 동일 = byte-identical noop (서버가 task_changed=true 라 주장해도 실제 변경 없음).
  const hasActualChange =
    !!taskBefore &&
    !!currentTask &&
    stableHashBlocks(taskBefore.blocks) !==
      stableHashBlocks(currentTask.blocks);
  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          px: "8px",
          py: "4px",
          fontSize: "11px",
          color: theme.vars.palette.Content.Weak,
        }}
      >
        <Box
          component="span"
          sx={{
            color: theme.vars.palette.primary.solid,
            fontWeight: 700,
          }}
        >
          ✎ 활동지 업데이트됨
        </Box>
        {hasActualChange && (
          <>
            <Box
              component="button"
              type="button"
              onClick={() => setDiffOpen(true)}
              sx={{
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
                textDecoration: "underline",
                color: theme.vars.palette.Content.Medium,
                fontSize: "11px",
                "&:hover": { color: theme.vars.palette.Content.Strong },
              }}
            >
              이번 턴의 변경 내용
            </Box>
            {onRevert && (
              <Box
                component="button"
                type="button"
                onClick={() => setConfirmRevertOpen(true)}
                sx={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  textDecoration: "underline",
                  color: theme.vars.palette.Content.Medium,
                  fontSize: "11px",
                  "&:hover": { color: "#b91c1c" },
                }}
              >
                되돌리기
              </Box>
            )}
          </>
        )}
      </Box>
      {hasActualChange && taskBefore && currentTask && (
        <TurnDiffModal
          open={diffOpen}
          taskBefore={taskBefore}
          taskAfter={currentTask}
          onClose={() => setDiffOpen(false)}
        />
      )}
      {onRevert && (
        <CustomModal
          open={confirmRevertOpen}
          onClose={() => setConfirmRevertOpen(false)}
          title="되돌리기"
          subtitle="이번 턴의 변경을 적용하기 직전 상태로 되돌릴까요? 이 작업은 취소할 수 없어요."
          actions={[
            {
              label: "되돌리기",
              variant: "Solid",
              color: "danger",
              onClick: () => {
                onRevert();
                setConfirmRevertOpen(false);
              },
            },
            {
              label: "취소",
              variant: "Plain",
              onClick: () => setConfirmRevertOpen(false),
            },
          ]}
        />
      )}
    </>
  );
}

// =========================
// DebugFooter
// =========================

export function DebugFooter({
  debug,
}: {
  debug: NonNullable<AssistantTurn["debug"]>;
}) {
  const copyTrace = () => {
    if (debug.trace_id && typeof navigator !== "undefined") {
      navigator.clipboard?.writeText(debug.trace_id);
    }
  };
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        mt: "4px",
        fontSize: "10px",
        color: theme.vars.palette.Content.Weak,
        fontFamily:
          "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      }}
    >
      <Box component="span">⏱ {formatDuration(debug.execution_time_ms)}</Box>
      {debug.trace_id && (
        <Box
          component="span"
          onClick={copyTrace}
          title="클릭해서 trace id 복사"
          sx={{
            cursor: "pointer",
            "&:hover": { color: theme.vars.palette.Content.Medium },
          }}
        >
          · trace {debug.trace_id}
        </Box>
      )}
    </Box>
  );
}

// =========================
// AssistantTurnView
// =========================

export function AssistantTurnView({
  turn,
  isLast,
  isStreaming,
  currentTask,
  onDecide,
  onRevertTurn,
  onRetry,
}: {
  turn: AssistantTurn;
  isLast: boolean;
  isStreaming: boolean;
  currentTask?: TaskEditorState;
  onDecide: (decisions: Decision[]) => void;
  onRevertTurn?: () => void;
  onRetry?: () => void;
}) {
  // 같은 turn 에서 propose_plan(계획) 승인이 선행됐는지 — update_task 카드의
  // "2/2단계" 표기에 쓰인다.
  const precededByPlan = turn.steps.some(
    (s) =>
      s.kind === "approval_decision" && s.originalAction.name === "propose_plan"
  );
  const turnActive = isStreaming && isLast && turn.status === "streaming";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        animation: "fadeInUp 0.25s ease",
        "@keyframes fadeInUp": {
          from: { opacity: 0, transform: "translateY(4px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
      }}
    >
      {turn.steps.map((step, idx) => {
        const isLastStep = idx === turn.steps.length - 1;
        if (step.kind === "tool") {
          // turn 이 실제로 streaming 중일 때만 스피너. 종료된 turn 의 미완료
          // tool(예: 실패한 update_task) 은 정적 마커로 멈춘다.
          return <ToolCallRow key={step.id} step={step} active={turnActive} />;
        }
        if (step.kind === "text") {
          return (
            <TextStreamBubble
              key={step.id}
              step={step}
              isLast={isLast && isLastStep}
              isStreaming={isStreaming && isLast && isLastStep}
            />
          );
        }
        return <ApprovalDecisionRow key={step.id} step={step} />;
      })}

      {turn.todos && turn.todos.length > 0 && (
        <TodoChecklist
          todos={turn.todos}
          active={turnActive}
          failed={turn.status === "error"}
        />
      )}

      {isLast && isStreaming && turn.status === "streaming" && (
        <ThinkingIndicator />
      )}

      {turn.status === "awaiting_approval" && turn.pendingApproval && (
        <ApprovalCard
          data={turn.pendingApproval}
          onDecide={onDecide}
          precededByPlan={precededByPlan}
        />
      )}

      {turn.taskChanged && (
        <TaskPatchPill
          taskBefore={turn.taskBefore}
          currentTask={currentTask}
          onRevert={onRevertTurn}
        />
      )}

      {turn.status === "cancelled" && (
        <Box
          sx={{
            fontSize: "12px",
            color: theme.vars.palette.Content.Weak,
            px: "2px",
          }}
        >
          답변을 중단했어요
        </Box>
      )}

      {turn.error && (
        <Box
          sx={{
            p: "8px 10px",
            borderRadius: "6px",
            backgroundColor: "rgba(239, 68, 68, 0.08)",
            border: `1px solid rgba(239, 68, 68, 0.3)`,
            fontSize: "12px",
            color: "#b91c1c",
          }}
        >
          {turn.error.message}
        </Box>
      )}

      {turn.error?.type === "timeout" && onRetry && (
        <Box
          component="button"
          type="button"
          onClick={onRetry}
          disabled={isStreaming}
          sx={{
            mt: "6px",
            alignSelf: "flex-start",
            px: "10px",
            py: "5px",
            borderRadius: "6px",
            border: `1px solid ${theme.vars.palette.Border.Strong}`,
            backgroundColor: theme.vars.palette.Surface.Forward,
            cursor: "pointer",
            fontSize: "12px",
            fontWeight: 600,
            color: theme.vars.palette.Content.Strong,
            transition: "background-color 0.12s ease",
            "&:hover:not(:disabled)": {
              backgroundColor: theme.vars.palette.Background.Level1,
            },
            "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
          }}
        >
          다시 시도
        </Box>
      )}

      {turn.debug && <DebugFooter debug={turn.debug} />}
    </Box>
  );
}
